// ExpiryStrip — the chain card's Deribit-style selector: a horizontal row of
// expiry chips (chronological, left->right), each showing the expiry date with
// that expiry's volume beneath it (premium + trades) and a small proportional
// volume bar (scaled to the max premium across the strip). The selected chip is
// highlighted. Overflows into a horizontally-scrollable, app-themed scrollbar.
//
// Callers pass an already future-filtered, chronological list.

import { Box, Stack } from '@mui/material';

import { tokens } from '../../../../styles/tokens';
import {
  fmtCount,
  fmtExpiryChipDate,
  fmtUsd,
} from '../../../../providers/options-flow/options-flow.helpers';
import { ChainExpiry } from '../../../../providers/options-flow/types';
import { themedScrollbarSx } from '../table-chrome';

interface ExpiryStripProps {
  expiries: ChainExpiry[];
  activeExpiry: string | null;
  onSelect: (expiry: string) => void;
}

export const ExpiryStrip = ({ expiries, activeExpiry, onSelect }: ExpiryStripProps) => {
  const maxPremium = Math.max(1, ...expiries.map((e) => e.premium_usd));

  return (
    <Stack
      direction="row"
      spacing={0.75}
      sx={{
        overflowX: 'auto',
        pb: '6px',
        mb: '10px',
        borderBottom: `1px solid ${tokens.border.default}`,
        ...themedScrollbarSx,
      }}
    >
      {expiries.map((e) => {
        const active = e.expiry === activeExpiry;
        return (
          <Box
            key={e.expiry_date + e.expiry}
            role="button"
            tabIndex={0}
            aria-selected={active}
            onClick={() => onSelect(e.expiry)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') onSelect(e.expiry);
            }}
            sx={{
              cursor: 'pointer',
              flex: '0 0 auto',
              width: 78,
              px: '8px',
              py: '6px',
              borderRadius: '6px',
              border: `1px solid ${active ? tokens.flow.accent : tokens.border.default}`,
              backgroundColor: active ? tokens.bg.widget : 'transparent',
              '&:hover': { backgroundColor: tokens.bg.widget },
            }}
          >
            <Box
              sx={{
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                fontSize: 10,
                fontWeight: 700,
                color: active ? tokens.flow.accent : tokens.text.secondary,
                whiteSpace: 'nowrap',
              }}
            >
              {fmtExpiryChipDate(e.expiry_date)}
            </Box>
            <Box sx={{ fontSize: 9.5, color: tokens.text.primary, mt: '3px' }}>
              {fmtUsd(e.premium_usd)}
            </Box>
            {/* Proportional volume bar (premium share of the strip max). */}
            <Box
              sx={{
                mt: '3px',
                height: 3,
                borderRadius: '2px',
                backgroundColor: tokens.bg.surfaceDark,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${(e.premium_usd / maxPremium) * 100}%`,
                  height: '100%',
                  backgroundColor: active ? tokens.flow.accent : tokens.flow.buy,
                }}
              />
            </Box>
            <Box sx={{ fontSize: 8.5, color: tokens.text.muted, mt: '3px' }}>
              {fmtCount(e.trades)} trades
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};
